import { FC, useEffect, useRef, useState } from "react";
import { dispatch } from "../../dispatch/dispatch";
import { actions } from "../../dispatch";
import getDate from "../../getDate";
import { countOrders, t } from "../../i18n/ar";

// ─── "We packed everything — print the labels" ─────────────────────────────
// One big button at the end of the packing view. Two taps (button → confirm)
// so it cannot fire by accident, then the run is polled until it finishes
// and the per-store outcome is shown in plain Arabic. The labels themselves
// are emailed to the printing mailbox configured per store in the dashboard.

type Outcome = "sent" | "nothing-to-print" | "no-emails-configured" | "token-missing" | "token-expired" | "error";

interface IReport {
    startedAt: string;
    finishedAt: string | null;
    source?: string;
    stores: Array<{ name: string; outcome: Outcome; detail?: string }>;
}

interface IStatus {
    running: boolean;
    report: IReport | null;
}

type Phase = "idle" | "confirm" | "sending" | "done" | "failed" | "timeout";

const outcomeAr = (o: Outcome) => {
    switch (o) {
        case "sent": return t.outcomeSent;
        case "nothing-to-print": return t.outcomeNothing;
        case "no-emails-configured": return t.outcomeNoEmail;
        case "token-missing":
        case "token-expired": return t.outcomeToken;
        default: return t.outcomeError;
    }
};

const outcomeCls = (o: Outcome) =>
    o === "sent"
        ? "bg-emerald-100 text-emerald-800"
        : o === "nothing-to-print"
        ? "bg-slate-100 text-slate-600"
        : "bg-red-100 text-red-700";

const POLL_MS = 4000;
const MAX_WAIT_MS = 6 * 60 * 1000;

export const PrintLabels: FC<{ remainingOrders: number; heldOrders?: number }> = ({ remainingOrders, heldOrders = 0 }) => {
    const [phase, setPhase] = useState<Phase>("idle");
    const [report, setReport] = useState<IReport | null>(null);
    const timer = useRef<number | null>(null);

    const stopPolling = () => {
        if (timer.current) window.clearInterval(timer.current);
        timer.current = null;
    };
    useEffect(() => stopPolling, []);

    // Show the last run (even one started from the dashboard) on mount.
    useEffect(() => {
        (async () => {
            const s: IStatus | { error: any } = await dispatch(actions.sendLabelsStatus);
            if (s && !("error" in s)) {
                if (s.running) startPolling();
                else if (s.report) setReport(s.report);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const startPolling = () => {
        setPhase("sending");
        stopPolling();
        const started = Date.now();
        timer.current = window.setInterval(async () => {
            const s: IStatus | { error: any } = await dispatch(actions.sendLabelsStatus);
            if (!s || "error" in s) return;
            if (!s.running && s.report?.finishedAt) {
                stopPolling();
                setReport(s.report);
                const anySent = s.report.stores.some((x) => x.outcome === "sent");
                const anyBad = s.report.stores.some((x) => !["sent", "nothing-to-print"].includes(x.outcome));
                setPhase(anySent ? "done" : anyBad ? "failed" : "done");
            } else if (Date.now() - started > MAX_WAIT_MS) {
                stopPolling();
                setPhase("timeout");
            }
        }, POLL_MS);
    };

    const send = async () => {
        const resp = await dispatch(actions.sendLabels);
        if (!resp || "error" in resp) {
            // 409 = a run is already going — just follow it
            if (/already/i.test(String(resp?.error?.message || ""))) return startPolling();
            setPhase("failed");
            return;
        }
        startPolling();
    };

    const sentCount = report ? report.stores.filter((s) => s.outcome === "sent").length : 0;

    return (
        <section className="mt-10 rounded-3xl border-4 border-emerald-300 bg-emerald-50 p-4 shadow-lg sm:p-6">
            <h2 className="flex items-center gap-2 text-2xl font-black text-emerald-900">
                <span aria-hidden>🏷️🖨️</span> {t.labelsTitle}
            </h2>

            {phase === "idle" && (
                <>
                    <button
                        onClick={() => setPhase("confirm")}
                        className="mt-4 w-full rounded-3xl bg-emerald-600 px-4 py-5 text-2xl font-black leading-snug text-white shadow-xl transition-colors hover:bg-emerald-500 active:bg-emerald-700 sm:text-3xl"
                    >
                        ✅ {t.labelsBtn}
                    </button>
                    <p className="mt-2 text-center text-base font-bold text-emerald-800/80">{t.labelsBtnSub}</p>
                    {heldOrders > 0 && (
                        <p className="mt-1 text-center text-base font-black text-amber-700">⏸ {t.labelsHeldNote} — {countOrders(heldOrders)}</p>
                    )}
                </>
            )}

            {phase === "confirm" && (
                <div className="mt-4 rounded-2xl border-2 border-amber-400 bg-white p-4">
                    <p className="text-xl font-black leading-relaxed text-slate-800">❓ {t.labelsConfirm}</p>
                    {remainingOrders > 0 && (
                        <p className="mt-3 rounded-xl bg-amber-100 px-3 py-2 text-lg font-black text-amber-800">
                            ⚠️ {t.labelsRemainingWarn} — {countOrders(remainingOrders)}
                        </p>
                    )}
                    <div className="mt-4 grid grid-cols-2 gap-3">
                        <button
                            onClick={() => setPhase("idle")}
                            className="rounded-2xl border-2 border-slate-300 bg-white px-4 py-4 text-xl font-black text-slate-700 hover:bg-slate-50"
                        >
                            ↩️ {t.labelsNo}
                        </button>
                        <button
                            onClick={send}
                            className="rounded-2xl bg-emerald-600 px-4 py-4 text-xl font-black text-white shadow hover:bg-emerald-500"
                        >
                            📧 {t.labelsYes}
                        </button>
                    </div>
                </div>
            )}

            {phase === "sending" && (
                <p className="mt-4 animate-pulse rounded-2xl bg-sky-100 px-4 py-5 text-center text-2xl font-black text-sky-800">
                    ⏳ {t.labelsSending}
                </p>
            )}

            {phase === "done" && (
                <p className="mt-4 rounded-2xl bg-emerald-600 px-4 py-5 text-center text-2xl font-black text-white">
                    {sentCount > 0 ? t.labelsDone : t.labelsNothing}
                </p>
            )}
            {phase === "failed" && (
                <p className="mt-4 rounded-2xl bg-red-600 px-4 py-5 text-center text-2xl font-black text-white">⛔ {t.labelsFailed}</p>
            )}
            {phase === "timeout" && (
                <p className="mt-4 rounded-2xl bg-amber-400 px-4 py-5 text-center text-xl font-black text-slate-900">⏳ {t.labelsTimeout}</p>
            )}

            {/* Per-store outcome of the last run */}
            {report && phase !== "sending" && (
                <div className="mt-4 rounded-2xl bg-white p-3">
                    <p className="text-sm font-bold text-slate-500">
                        🕐 {t.labelsLastRun}: <span className="ltr">{getDate(new Date(report.startedAt), "DD/MM HH:mm")}</span>
                    </p>
                    <div className="mt-2 space-y-1">
                        {report.stores.map((s) => (
                            <div key={s.name} className="flex items-center justify-between gap-2 rounded-lg px-2 py-1">
                                <span className="ltr truncate text-start text-base font-bold text-slate-700">🏪 {s.name}</span>
                                <span className={`shrink-0 rounded-lg px-2 py-0.5 text-sm font-black ${outcomeCls(s.outcome)}`}>
                                    {outcomeAr(s.outcome)}
                                </span>
                            </div>
                        ))}
                    </div>
                    {phase !== "idle" && (
                        <button
                            onClick={() => setPhase("idle")}
                            className="mt-3 w-full rounded-xl border-2 border-slate-300 px-3 py-2 text-lg font-black text-slate-600 hover:bg-slate-50"
                        >
                            🔄 {t.labelsTitle}
                        </button>
                    )}
                </div>
            )}
        </section>
    );
};
